/**
 * Project tabs (docs/ux/project-tab-spec.md §1.1): the account's pinned projects, in order. The
 * order lives in each encrypted project entity (`tab.order`) so every device shows the same
 * ⌘-numbers. Pure helpers: which tabs show, the one-time bootstrap, and reorder/pin writes.
 */
export interface TabProject {
  id: string
  name: string
  status?: string
  tab?: { order: number | null }
}
export interface TabWrite {
  id: string
  order: number | null
}

/** Tabs bound to ⌘/⌥1–9; the rest go to the overflow menu. */
export const TAB_KEYS = 9

const active = (p: TabProject) => p.status !== 'archived'

/** No project has ever had a tab position written (first load after the Lanes tab shipped). */
export function needsBootstrap(projects: readonly TabProject[]): boolean {
  return projects.length > 0 && projects.every((p) => p.tab === undefined)
}

/**
 * First-load order: up to nine active projects pinned in last-activity order (newest first), the
 * rest unpinned. Ties keep the listed order.
 */
export function bootstrapOrder(
  projects: readonly TabProject[],
  lastActivity: ReadonlyMap<string, number>,
): TabWrite[] {
  const ranked = projects
    .map((p, i) => ({ p, i }))
    .filter(({ p }) => active(p))
    .sort((a, b) => (lastActivity.get(b.p.id) ?? 0) - (lastActivity.get(a.p.id) ?? 0) || a.i - b.i)
  const pinned = new Set(ranked.slice(0, TAB_KEYS).map(({ p }) => p.id))
  const order = ranked.map(({ p }) => p.id)
  return projects.map((p) => ({ id: p.id, order: pinned.has(p.id) ? order.indexOf(p.id) : null }))
}

/**
 * The tab strip: pinned active projects by order. Before any order exists (or when the stored
 * order is unavailable), `fallback` supplies it so the strip never renders empty.
 */
export function pinnedTabs<P extends TabProject>(
  projects: readonly P[],
  fallback?: readonly TabWrite[],
): P[] {
  const fb = new Map(fallback?.map((w) => [w.id, w.order]))
  const orderOf = (p: P) => (p.tab ? p.tab.order : (fb.get(p.id) ?? null))
  return projects
    .filter((p) => active(p) && orderOf(p) !== null)
    .sort((a, b) => (orderOf(a) ?? 0) - (orderOf(b) ?? 0))
}

/** Active projects that are not pinned (the `⋯` menu / ⌘K). */
export function unpinnedProjects<P extends TabProject>(
  projects: readonly P[],
  pinned: readonly P[],
): P[] {
  const ids = new Set(pinned.map((p) => p.id))
  return projects.filter((p) => active(p) && !ids.has(p.id))
}

/** Writes that renumber `ids` 0..n-1, skipping projects whose order is already right. */
function renumber(ids: readonly string[], current: ReadonlyMap<string, number | null>): TabWrite[] {
  return ids.map((id, order) => ({ id, order })).filter((w) => current.get(w.id) !== w.order)
}

const orders = (projects: readonly TabProject[]) =>
  new Map(projects.map((p) => [p.id, p.tab?.order ?? null]))

/** Move pinned tab `id` to position `to` (clamped). */
export function moveTab(pinned: readonly TabProject[], id: string, to: number): TabWrite[] {
  const ids = pinned.map((p) => p.id)
  const from = ids.indexOf(id)
  if (from < 0) return []
  ids.splice(from, 1)
  ids.splice(Math.max(0, Math.min(to, ids.length)), 0, id)
  return renumber(ids, orders(pinned))
}

export function unpinTab(pinned: readonly TabProject[], id: string): TabWrite[] {
  if (!pinned.some((p) => p.id === id)) return []
  const rest = pinned.filter((p) => p.id !== id)
  return [
    { id, order: null },
    ...renumber(
      rest.map((p) => p.id),
      orders(rest),
    ),
  ]
}

export function pinTab(pinned: readonly TabProject[], id: string): TabWrite[] {
  if (pinned.some((p) => p.id === id)) return []
  return [{ id, order: pinned.length }]
}

/** Tab index (0-based) after a ⌥⇧←/→ step, or null at an edge. */
export function stepIndex(index: number, dir: -1 | 1, count: number): number | null {
  const next = index + dir
  return next < 0 || next >= count ? null : next
}

/** Newest run activity per project (the bootstrap's ordering signal). */
export function lastActivity(
  runs: readonly { projectId?: string; updatedAt?: number }[],
): Map<string, number> {
  const out = new Map<string, number>()
  for (const r of runs)
    if (r.projectId) out.set(r.projectId, Math.max(out.get(r.projectId) ?? 0, r.updatedAt ?? 0))
  return out
}
